import { createReducer } from '@reduxjs/toolkit';
import {
    getAllProducts,
    getPrimaryOrSecondaryProducts,
    getSelectedProduct
} from './shipping-selectors';
import { ensureGoodShippingOptions } from './ensure-good-shipping-options';
import { validate } from './validate';
import { getSelectedShippingOptions } from './get-selected-shipping-options';

const initialState = validate({
    primaryShippingProviders: [],
    secondaryShippingProviders: [],
    validationErrors: {},
    globalValidationError: null,
    primaryValidationError: null,
    secondaryValidationError: null,
    secondaryShippingOptionOpen: false,
    selectedShippingOptions: [],
    showPrimaryShippingCalculator: false,
    hasPrimaryCalculatorSuggestions: false,
    showSecondaryShippingCalculator: false,
    hasSecondaryCalculatorSuggestions: false,
    takeawayDeliveryAccepted: false,
    isLoadingProvidersWithProducts: false,
    defaultCalculatorDimensions: {
        height: 0,
        width: 0,
        length: 0,
        weight: 0
    },
    primaryCalculatorDimensions: null,
    secondaryCalculatorDimensions: null,
    forceFreeShipping: false,
    fromCountry: null
});

const getProviderByProductType = (providers, productType) =>
    providers.find(
        provider =>
            !!provider.products.find(product => product.type === productType)
    );

export default createReducer(initialState, {
    SET_SUGGESTED_SHIPPING_PRODUCTS: (state, action) => {
        const products = getPrimaryOrSecondaryProducts(
            state,
            action.payload.primary
        );
        products.forEach(product => {
            // Clear previous selections
            product.selected = false;
            product.selectedVariant = null;
            product.productId = null;
            product.cost = state.forceFreeShipping ? 0 : null;
            product.weight = null;

            const suggestedProduct = action.payload.shippingProducts.find(
                p => p.type === product.type
            );
            if (suggestedProduct) {
                product.calculatorSuggestion = {
                    description: suggestedProduct.description,
                    cost: suggestedProduct.cost,
                    additionalInfo: suggestedProduct.additionalInfo
                };
            } else {
                product.calculatorSuggestion = null;
            }
        });

        if (action.payload.primary) {
            state.hasPrimaryCalculatorSuggestions = true;
        } else {
            state.hasSecondaryCalculatorSuggestions = true;
        }

        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    CLEAR_SUGGESTED_SHIPPING_PRODUCTS: (state, action) => {
        const products = getPrimaryOrSecondaryProducts(
            state,
            action.payload.primary
        );
        products.forEach(product => {
            const suggestion = product.calculatorSuggestion;
            product.calculatorSuggestion = null;
            if (product.selected) {
                // Clear automatic calculator suggestion selections,
                // but save selection for selected product if changed.
                if (!suggestion) {
                    // No values are set from suggestion. Save selection
                    return;
                }
                if (
                    product.selectedVariant &&
                    product.selectedVariant.price !== suggestion.cost
                ) {
                    // Values are changes from suggestion. Save selection
                    return;
                }
                if (
                    product.selectedVariant?.price === suggestion.cost &&
                    (product.cost || product.cost === 0)
                ) {
                    // Own price set. Save selection
                    return;
                }
                if (
                    !product.selectedVariant &&
                    product.cost !== suggestion.cost
                ) {
                    // Own price set. Save selection
                    return;
                }
                // No values are manually changed. Go ahead and remove selection
            }
            product.selectedVariant = null;
            product.productId = null;
            product.cost = state.forceFreeShipping ? 0 : null;
            product.weight = null;
        });

        if (action.payload.primary) {
            state.hasPrimaryCalculatorSuggestions = false;
        } else {
            state.hasSecondaryCalculatorSuggestions = false;
        }

        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    RESET_SHIPPING_OPTIONS: state => {
        // TODO: primaryShippingOption is never set. Probably remove.
        state.primaryShippingOption = null;
        state.secondaryShippingOption = null;
        state.selectedShippingOptions = [];
        return validate(state);
    },
    RESET_SELECTED_SHIPPING_OPTIONS: state => {
        const products = getAllProducts(state);
        products.forEach(product => {
            product.selected = false;
            product.selectedVariant = null;
            product.productId = null;
            product.cost = null;
            product.weight = null;
        });
        state.forceFreeShipping = false;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_SHIPPING_OPTIONS: (state, action) => {
        const shippingOptions = ensureGoodShippingOptions(action.payload);

        // Set a product to visible and hides the rest, if the type to be visible is found in the list.
        const updateVisibilities = (products, typeToBeVisible) => {
            if (products.length === 1) return;
            const typeToBeVisibleExists = products.find(
                product => product.type === typeToBeVisible
            );
            if (!typeToBeVisibleExists) return;
            products.forEach(
                product => (product.visible = product.type === typeToBeVisible)
            );
        };

        const updateProductIfType = (
            product,
            { type, cost, productId, weight }
        ) => {
            let isProductForOption = null;
            if (!product.variants || product.variants.length === 0) {
                isProductForOption = product.type === type;
            } else {
                isProductForOption = !!product.variants.find(
                    x => x.id === productId && x.type === type
                );
            }
            if (isProductForOption) {
                product.selected = true;
                product.cost = cost;
                product.productId = productId;
                product.weight = weight;
            } else {
                product.selected = false;
            }
        };

        const updateProviderProducts = (products, option) => {
            products.forEach(product => updateProductIfType(product, option));
            updateVisibilities(products, option.type);
        };

        if (typeof shippingOptions[0] === 'object') {
            const option = shippingOptions[0];
            const providers = state.primaryShippingProviders;
            providers.forEach(provider =>
                updateProviderProducts(provider.products, option)
            );
        }

        if (typeof shippingOptions[1] === 'object') {
            const option = shippingOptions[1];
            const providers = state.secondaryShippingProviders;
            providers.forEach(provider =>
                updateProviderProducts(provider.products, option)
            );
            const provider = getProviderByProductType(providers, option.type);
            state.showMoreSecondaryOptions =
                state.showMoreSecondaryOptions ||
                (provider && !provider.promoted);
            state.secondaryShippingOptionOpen = true;
        } else {
            state.secondaryShippingOptionOpen = false;
        }

        state.selectedShippingOptions = getSelectedShippingOptions(state);

        return validate(state);
    },
    TOGGLE_SHIPPING_OPTION: (state, action) => {
        const providers = action.payload.primary
            ? state.primaryShippingProviders
            : state.secondaryShippingProviders;
        const showShippingCalculator = action.payload.primary
            ? state.showPrimaryShippingCalculator
            : state.showSecondaryShippingCalculator;

        // Hide products so only one is visible per provider
        const productProvider = getProviderByProductType(
            providers,
            action.payload.type
        );
        productProvider.products.forEach(product => (product.visible = false));

        const products = providers.flatMap(provider => provider.products);
        const product = products.find(p => p.type === action.payload.type);
        const previousValue = product.selected;
        products.forEach(p => (p.selected = false));
        product.selected = !previousValue;
        product.visible = true;

        if (showShippingCalculator) {
            const suggestedProduct = product.calculatorSuggestion;
            if (suggestedProduct) {
                if (
                    product.variants?.some(
                        variant => variant.price === suggestedProduct.cost
                    )
                ) {
                    const matchingVariant = product.variants.find(
                        variant => variant.price === suggestedProduct.cost
                    );
                    product.selectedVariant = matchingVariant;
                    product.productId = matchingVariant.id;
                    product.weight = matchingVariant.weight;
                } else {
                    product.cost = state.forceFreeShipping
                        ? 0
                        : suggestedProduct.cost;
                }
            } else {
                // Clear previous selections
                product.selectedVariant = null;
                product.productId = null;
                product.cost = state.forceFreeShipping ? 0 : null;
                product.weight = null;
            }
        }

        if (product.variants.length === 1) {
            const variant = product.variants[0];
            product.selectedVariant = variant;
            product.productId = variant.id;
            product.weight = variant.weight;
        }

        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_SHIPPING_OPTION_COST: (state, action) => {
        const product = getSelectedProduct(state, action.payload.primary);
        product.cost = action.payload.cost;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_SHIPPING_OPTION_VARIANT: (state, action) => {
        const product = getSelectedProduct(state, action.payload.primary);

        product.selectedVariant = action.payload.variant;
        product.productId = action.payload.variant?.id;
        product.weight = action.payload.variant?.weight;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_TAKEAWAY_SELECTED: (state, action) => {
        state.takeawayDeliveryAccepted = action.payload.takeawaySelected;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_SECONDARY_SHIPPING_OPTION_OPEN: (state, action) => {
        return validate({
            ...state,
            secondaryShippingOptionOpen:
                action.payload.secondaryShippingOptionOpen
        });
    },
    REMOVE_SHIPPING_OPTION: (state, action) => {
        getPrimaryOrSecondaryProducts(state, action.payload.primary).forEach(
            product => (product.selected = false)
        );
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    MAKE_SECONDARY_SHIPPING_OPTION_PRIMARY: state => {
        const tmp = state.primaryShippingProviders;
        state.primaryShippingProviders = state.secondaryShippingProviders;
        state.secondaryShippingProviders = tmp;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    FORCE_FREE_SHIPPING: state => {
        const products = getAllProducts(state);
        products.forEach(product => {
            product.cost = 0;
        });
        state.forceFreeShipping = true;
        state.selectedShippingOptions = getSelectedShippingOptions(state);
        return validate(state);
    },
    SET_SHIPPING_PROVIDERS_WITH_PRODUCTS: (state, action) => {
        state.primaryShippingProviders = action.payload;
        state.secondaryShippingProviders = action.payload;
        state.isLoadingProvidersWithProducts = false;

        return validate(state);
    },
    SET_SHIPPING_PROVIDERS_WITH_PRODUCTS_FETCHING: state => {
        state.isLoadingProvidersWithProducts = true;

        return validate(state);
    },
    SET_SHIPPING_CALCULATOR_DIMENSIONS: (state, action) => {
        state.defaultCalculatorDimensions = {
            ...initialState.defaultCalculatorDimensions,
            ...action.payload.dimensions
        };
        state[
            action.payload.primary
                ? 'primaryCalculatorDimensions'
                : 'secondaryCalculatorDimensions'
        ] = action.payload.dimensions;
    },
    SET_SHOW_SHIPPING_CALCULATOR: (state, action) => {
        state[
            action.payload.primary
                ? 'showPrimaryShippingCalculator'
                : 'showSecondaryShippingCalculator'
        ] = action.payload.value;
    },
    SET_SHIPPING_FROMCOUNTRY: (state, action) => {
        state.fromCountry = action.payload.fromCountry;
    }
});
